/**
 * Vendor APIs — the connected courier vendor APIs (Worldfirst, ICL, Atlantic,
 * and any other Xpresion-based vendor).
 *
 * Settings saved here override the code defaults and env.json and take effect
 * immediately. Passwords are write-only: the server never sends them back, it
 * only says where the active one comes from (saved here, or env.json).
 * "Test connection" is a read-only tracking lookup — it never books anything.
 */
import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNotification } from "context/notification.context";
import { getVendorApis, addVendorApi, updateVendorApi, resetVendorApi, testVendorApi } from "network/service";

const BLANK = {
  isNew: true, key: "", label: "", base_url: "", user_id: "", customer_code: "", default_origin: "",
  legacy_ssl: false, booking_capable: true, is_active: true, notes: "", password: "",
};

const PW_LABEL = { database: "Saved here", "env.json": "From env.json", none: "Not set" };

const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

function Detail({ label, value }) {
  return (
    <MDBox mb={0.75}>
      <MDTypography variant="caption" color="text" display="block">{label}</MDTypography>
      <MDTypography variant="button" fontWeight="medium" sx={{ wordBreak: "break-all" }}>{value || "—"}</MDTypography>
    </MDBox>
  );
}

export default function VendorApis() {
  const { showNotification } = useNotification();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [resetting, setResetting] = useState(null);

  const load = () => {
    setLoading(true);
    getVendorApis()
      .then((r) => setVendors(r.data || []))
      .catch(() => showNotification("error", "Error", "Failed to load vendor APIs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const test = (v) => {
    setTesting((t) => ({ ...t, [v.key]: true }));
    testVendorApi(v.key)
      .then((r) => {
        showNotification(r.data.ok ? "success" : "error", v.label, `${r.data.message} (${r.data.ms} ms)`);
        load();
      })
      .catch((err) => showNotification("error", v.label, err?.response?.data?.message || "Test failed"))
      .finally(() => setTesting((t) => ({ ...t, [v.key]: false })));
  };

  const save = () => {
    const e = editing;
    const payload = {
      label: e.label, base_url: e.base_url, user_id: e.user_id, customer_code: e.customer_code,
      default_origin: e.default_origin, legacy_ssl: !!e.legacy_ssl, booking_capable: !!e.booking_capable,
      is_active: !!e.is_active, notes: e.notes,
      ...(e.password ? { password: e.password } : {}),
    };
    setSaving(true);
    const req = e.isNew ? addVendorApi({ ...payload, key: e.key || e.label }) : updateVendorApi(e.key, payload);
    req
      .then(() => {
        showNotification("success", "Saved", `${e.label || e.key} saved — active immediately`);
        setEditing(null);
        load();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Save failed"))
      .finally(() => setSaving(false));
  };

  const reset = () => {
    const v = resetting;
    resetVendorApi(v.key)
      .then((r) => {
        showNotification("success", v.label, r.data.reverted_to_defaults ? "Reverted to defaults" : "Removed");
        setResetting(null);
        load();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Failed"));
  };

  const set = (patch) => setEditing((e) => ({ ...e, ...patch }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
          <MDBox>
            <MDTypography variant="h5" fontWeight="bold">Vendor APIs</MDTypography>
            <MDTypography variant="caption" color="text">
              Connected courier vendor APIs used for vendor booking and internal tracking. Changes apply immediately.
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <MDButton variant="outlined" color="secondary" onClick={load} disabled={loading}>
              <Icon>refresh</Icon>&nbsp;Reload
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={() => setEditing({ ...BLANK })}>
              <Icon>add</Icon>&nbsp;Add vendor API
            </MDButton>
          </MDBox>
        </MDBox>

        <Grid container spacing={3}>
          {vendors.map((v) => (
            <Grid item xs={12} md={6} xl={4} key={v.key}>
              <Card sx={{ p: 2.5, height: "100%", opacity: v.is_active ? 1 : 0.7 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <MDBox>
                    <MDTypography variant="h6">{v.label}</MDTypography>
                    <MDTypography variant="caption" color="text">key: {v.key}</MDTypography>
                  </MDBox>
                  <Chip
                    size="small"
                    label={v.is_active ? "Active" : "Disabled"}
                    color={v.is_active ? "success" : "default"}
                  />
                </MDBox>

                <MDBox display="flex" gap={0.75} flexWrap="wrap" my={1.5}>
                  <Chip size="small" variant="outlined" label={v.booking_capable ? "Booking + tracking" : "Tracking only"} />
                  <Chip size="small" variant="outlined" label={v.built_in ? (v.customised ? "Built-in · customised" : "Built-in") : "Custom"} />
                  <Chip
                    size="small"
                    variant="outlined"
                    color={v.has_password ? "default" : "error"}
                    label={`Password: ${PW_LABEL[v.password_source] || v.password_source}`}
                  />
                  {v.legacy_ssl && <Chip size="small" variant="outlined" label="Legacy SSL" />}
                </MDBox>

                <Divider />
                <Grid container spacing={1}>
                  <Grid item xs={12}><Detail label="Base URL" value={v.base_url} /></Grid>
                  <Grid item xs={6}><Detail label="User ID" value={v.user_id} /></Grid>
                  <Grid item xs={6}><Detail label="Customer code" value={v.customer_code} /></Grid>
                  <Grid item xs={6}><Detail label="Default origin" value={v.default_origin} /></Grid>
                  <Grid item xs={6}>
                    <Detail label="Used by tracking vendors" value={v.used_by.length ? v.used_by.join(", ") : ""} />
                  </Grid>
                </Grid>
                {v.notes ? (
                  <MDTypography variant="caption" color="text" display="block" mt={0.5}>📝 {v.notes}</MDTypography>
                ) : null}

                <MDBox
                  mt={1.5}
                  p={1}
                  sx={{
                    borderRadius: 1.5,
                    background: v.last_test_at ? (v.last_test_ok ? "#e8f5e9" : "#fdecea") : "#f5f5f5",
                  }}
                >
                  <MDTypography variant="caption" fontWeight="medium" display="block">
                    {v.last_test_at
                      ? `${v.last_test_ok ? "✓" : "✗"} ${v.last_test_message}`
                      : "Not tested yet"}
                  </MDTypography>
                  {v.last_test_at && (
                    <MDTypography variant="caption" color="text">Last tested {fmt(v.last_test_at)}</MDTypography>
                  )}
                </MDBox>
                {v.updated_by ? (
                  <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                    Last changed by {v.updated_by} · {fmt(v.updated_at)}
                  </MDTypography>
                ) : null}

                <MDBox display="flex" gap={1} mt={2} flexWrap="wrap">
                  <MDButton size="small" variant="gradient" color="info" disabled={testing[v.key]} onClick={() => test(v)}>
                    {testing[v.key] ? <CircularProgress size={14} color="inherit" /> : <Icon>network_check</Icon>}
                    &nbsp;Test connection
                  </MDButton>
                  <MDButton size="small" variant="outlined" color="dark" onClick={() => setEditing({ ...v, password: "", isNew: false })}>
                    <Icon>edit</Icon>&nbsp;Edit
                  </MDButton>
                  {v.customised && (
                    <MDButton size="small" variant="text" color="error" onClick={() => setResetting(v)}>
                      {v.built_in ? "Reset to defaults" : "Remove"}
                    </MDButton>
                  )}
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>
      </MDBox>

      {/* add / edit */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        {editing && (
          <>
            <DialogTitle>{editing.isNew ? "Add vendor API" : `Edit ${editing.label}`}</DialogTitle>
            <DialogContent dividers>
              <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                {editing.isNew && (
                  <MDTypography variant="caption" color="text">
                    For vendors on the Xpresion platform (same API as Worldfirst/ICL/Atlantic).
                  </MDTypography>
                )}
                <MDBox display="flex" gap={2}>
                  <MDInput label="Display name" value={editing.label} sx={{ flex: 2 }} onChange={(e) => set({ label: e.target.value })} />
                  {editing.isNew && (
                    <MDInput label="Key (optional)" value={editing.key} sx={{ flex: 1 }} onChange={(e) => set({ key: e.target.value })} />
                  )}
                </MDBox>
                <MDInput label="Base URL" placeholder="https://cloud.example.com" value={editing.base_url} onChange={(e) => set({ base_url: e.target.value })} />
                <MDBox display="flex" gap={2}>
                  <MDInput label="User ID" value={editing.user_id} sx={{ flex: 1 }} onChange={(e) => set({ user_id: e.target.value })} />
                  <MDInput
                    label="Password"
                    type="password"
                    autoComplete="new-password"
                    value={editing.password}
                    placeholder={editing.isNew ? "" : "Leave blank to keep current"}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                    onChange={(e) => set({ password: e.target.value })}
                  />
                </MDBox>
                <MDBox display="flex" gap={2}>
                  <MDInput label="Customer code" value={editing.customer_code} sx={{ flex: 1 }} onChange={(e) => set({ customer_code: e.target.value })} />
                  <MDInput label="Default origin" value={editing.default_origin} sx={{ flex: 1 }} onChange={(e) => set({ default_origin: e.target.value })} />
                </MDBox>
                <MDBox display="flex" gap={2} flexWrap="wrap">
                  <FormControlLabel
                    control={<Switch checked={!!editing.is_active} onChange={(e) => set({ is_active: e.target.checked })} />}
                    label={<MDTypography variant="button">Active</MDTypography>}
                  />
                  <FormControlLabel
                    control={<Switch checked={!!editing.booking_capable} onChange={(e) => set({ booking_capable: e.target.checked })} />}
                    label={<MDTypography variant="button">Allow bookings</MDTypography>}
                  />
                  <FormControlLabel
                    control={<Switch checked={!!editing.legacy_ssl} onChange={(e) => set({ legacy_ssl: e.target.checked })} />}
                    label={<MDTypography variant="button">Legacy SSL</MDTypography>}
                  />
                </MDBox>
                <MDInput label="Notes" multiline rows={2} value={editing.notes} onChange={(e) => set({ notes: e.target.value })} />
                {!editing.isNew && (
                  <MDTypography variant="caption" color="text">
                    Password is currently {PW_LABEL[editing.password_source] ? PW_LABEL[editing.password_source].toLowerCase() : editing.password_source}.
                    Only a new value you type here is saved.
                  </MDTypography>
                )}
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton variant="text" color="secondary" onClick={() => setEditing(null)}>Cancel</MDButton>
              <MDButton variant="gradient" color="info" disabled={saving} onClick={save}>
                {saving ? "Saving…" : "Save"}
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* reset / remove */}
      <Dialog open={!!resetting} onClose={() => setResetting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{resetting?.built_in ? "Reset to defaults?" : "Remove vendor API?"}</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="button" display="block">
            {resetting?.built_in
              ? `Discard the settings saved for ${resetting?.label}. It will go back to the built-in defaults and the env.json password.`
              : `Remove ${resetting?.label}. Bookings and tracking through it will stop working.`}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setResetting(null)}>Cancel</MDButton>
          <MDButton variant="gradient" color="error" onClick={reset}>{resetting?.built_in ? "Reset" : "Remove"}</MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
